import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Icon, type IconName } from '../../ui/Icon';

interface Metrics {
  requests_hoje: number;
  requests_24h: number;
  requests_7d: number;
  erros_24h: number;
  cache_hit_pct: number;
  ranker_escalated_pct: number;
  pendentes_revisao: number;
  tempo_medio_ms_24h: number | null;
  por_resultado_24h: Array<{ resultado: string; n: number }>;
}

export function Dashboard() {
  const { apiFetch, impersonate } = useAuth();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiFetch<{ ok: true; metrics: Metrics }>('/v1/admin/metrics/');
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar métricas');
      return;
    }
    setMetrics(r.data.metrics);
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHead
        title="Início"
        eyebrow="VISÃO GERAL"
        desc={
          impersonate
            ? `Métricas da empresa "${impersonate.nome}" (impersonate ativo).`
            : 'Status do sistema em tempo real.'
        }
        actions={
          <Button variant="ghost" icon="refresh" size="sm" onClick={load}>
            Atualizar
          </Button>
        }
      />

      {error && (
        <div
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
            fontSize: 13.5,
            marginBottom: 14
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 14,
          marginBottom: 20
        }}
      >
        {loading || !metrics ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 110 }} />
          ))
        ) : (
          <>
            <MetricCard
              icon="activity"
              label="Requests hoje"
              value={metrics.requests_hoje}
              hint={`${metrics.requests_7d} nos últimos 7d`}
            />
            <MetricCard
              icon="zap"
              label="Cache hit (24h)"
              value={`${metrics.cache_hit_pct}%`}
              hint={metrics.tempo_medio_ms_24h !== null ? `${metrics.tempo_medio_ms_24h}ms médio` : 'sem dados'}
              tone={metrics.cache_hit_pct >= 70 ? 'success' : metrics.cache_hit_pct >= 40 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon="sparkles"
              label="Ranker IA (24h)"
              value={`${metrics.ranker_escalated_pct}%`}
              hint="escalou para rank_text"
            />
            <MetricCard
              icon="image"
              label="Pendentes revisão"
              value={metrics.pendentes_revisao}
              hint="arquivos aguardando"
              tone={metrics.pendentes_revisao > 10 ? 'warning' : undefined}
            />
          </>
        )}
      </div>

      {!loading && metrics && metrics.por_resultado_24h.length > 0 && (
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            padding: '18px 20px',
            marginBottom: 16
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12
            }}
          >
            <h3 style={{ fontSize: 14 }}>Resultados nas últimas 24h</h3>
            <Badge color="neutral" mono>
              {metrics.requests_24h} requests
            </Badge>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {metrics.por_resultado_24h.map((r) => {
              const pct = metrics.requests_24h > 0 ? Math.round((r.n * 100) / metrics.requests_24h) : 0;
              const color =
                r.resultado === 'cache_hit'
                  ? 'var(--success)'
                  : r.resultado.startsWith('get_more')
                    ? 'var(--brand)'
                    : r.resultado === 'erro'
                      ? 'var(--danger)'
                      : 'var(--text-3)';
              return (
                <div key={r.resultado} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="mono" style={{ fontSize: 12, color: 'var(--text-2)', minWidth: 140 }}>
                    {r.resultado}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      height: 8,
                      borderRadius: 99,
                      background: 'var(--bg-2)',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: color,
                        transition: 'width 0.3s var(--ease)'
                      }}
                    />
                  </div>
                  <div className="tnum" style={{ fontSize: 12.5, color: 'var(--text-1)', minWidth: 60, textAlign: 'right' }}>
                    {r.n} · {pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && metrics && metrics.erros_24h > 0 && (
        <div
          style={{
            background: 'var(--danger-bg)',
            border: '1px solid color-mix(in oklch, var(--danger) 25%, transparent)',
            borderRadius: 'var(--r-md)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <Icon name="alert" size={18} style={{ color: 'var(--danger)' }} />
          <span style={{ fontSize: 13.5, color: 'var(--danger)' }}>
            <strong>{metrics.erros_24h}</strong> erros nas últimas 24h. Veja Logs para detalhes.
          </span>
        </div>
      )}
    </>
  );
}

function MetricCard({
  icon,
  label,
  value,
  hint,
  tone
}: {
  icon: IconName;
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'success' | 'warning' | 'danger';
}) {
  const toneColor =
    tone === 'success'
      ? 'var(--success)'
      : tone === 'warning'
        ? 'var(--warning)'
        : tone === 'danger'
          ? 'var(--danger)'
          : 'var(--text-0)';
  return (
    <div
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-3)' }}>
        <Icon name={icon} size={14} />
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </span>
      </div>
      <div className="tnum" style={{ fontSize: 28, fontWeight: 700, color: toneColor, letterSpacing: '-0.02em' }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{hint}</div>
      )}
    </div>
  );
}
